import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useKadiNiTidiGame } from '../hooks/useKadiNiTidiGame';
import type { 
  GameState, 
  Player, 
  Card,
  GamePhaseType,
  SuitType
} from '../types/game';

interface GameContextType {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  
  // Game state
  gameState: GameState | null;
  partnershipConfig?: string; // Partnership configuration from room state
  currentPlayer: Player | null;
  players: Player[];
  currentPhase: GamePhaseType;
  
  // Player's hand and game info
  playerCards: Card[];
  currentTrump: SuitType | null;
  currentTrick: any[];
  scores: Record<string, number>;
  
  // Actions
  connectToGame: (username: string, roomId?: string, enableAI?: boolean, authenticatedUserId?: string) => Promise<void>;
  joinSpecificRoom: (roomId: string, username: string, authenticatedUserId?: string) => Promise<void>;
  disconnect: () => void;
  makeBid: (bid: number, powerHouse: SuitType, partnerCards: Card[]) => void;
  passMove: () => void;
  playCard: (card: Card) => void;
  setReady: (ready: boolean) => void;
  requestAIPlayers: () => void;
  addAIPlayer: () => void;
  removeAIPlayer: () => void;
  restartGame: () => void;
  toggleAutoPlay: (enabled: boolean) => void;
  startFreshSession: () => void;
  
  // Room info
  roomInfo: { roomId: string; playerCount: number; frontendRoomId: string | null } | null;
  
  // Error handling
  setGameErrorHandler: (handler: (error: string) => void) => void;
}

const GameContext = createContext<GameContextType | null>(null);

interface GameProviderProps {
  children: ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const gameHook = useKadiNiTidiGame();
  
  return (
    <GameContext.Provider value={gameHook}>
      {children}
    </GameContext.Provider>
  );
};

export const useGameContext = (): GameContextType => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};